import { z } from 'zod';
import { CURRENCIES } from '../money/money';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * Money a customer paid us (PRD → Payments; gitaumoses4/chukua#333). The `receipts` table's channel CHECK hard-codes these three, so a
 * new channel is a migration as well as a `PaymentReferenceRule` for it.
 */
export const RECEIPT_CHANNELS = ['bank_transfer', 'cheque', 'mpesa'] as const;
export type ReceiptChannel = (typeof RECEIPT_CHANNELS)[number];

export const RECEIPT_CHANNEL_LABELS: Record<ReceiptChannel, string> = {
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
  mpesa: 'M-Pesa',
};

const MAX_TEXT_LENGTH = 200;

/** A calendar date as it appears on the statement, not an instant. A real day: 2026-02-31 would pass a shape check and fail in Postgres. */
const calendarDate = z.iso.date('Use a real date as YYYY-MM-DD');

const requiredText = z.string().trim().min(1).max(MAX_TEXT_LENGTH);

/**
 * Who recorded it is the signed-in user, never an input. A blank reference is the same as none: whether the channel needs one, and what
 * it must look like, is the channel's `PaymentReferenceRule` (the API's payment-reference seam), not this schema.
 */
export const receiptInputSchema = z
  .object({
    customerId: z.string().uuid(),
    channel: z.enum(RECEIPT_CHANNELS),
    reference: z
      .string()
      .trim()
      .max(MAX_TEXT_LENGTH)
      .optional()
      .transform((reference) => reference || undefined),
    amountMinor: z.number().int().positive(),
    currency: z.enum(CURRENCIES),
    receivedOn: calendarDate,
    payer: requiredText,
    /** Must be active and of a kind the channel takes (`accountKindsForChannel`); the API checks, this only checks the shape. */
    companyAccountId: z.string().uuid(),
  })
  .strict();
export type ReceiptInput = z.infer<typeof receiptInputSchema>;

/**
 * Derived from the receipt, its clearance and its reversal, never stored (gitaumoses4/chukua#334): only a cheque starts `pending`, and
 * `bounced` and `reversed` are the two causes of a reversal row.
 */
export const RECEIPT_STATUSES = ['pending', 'cleared', 'bounced', 'reversed'] as const;
export type ReceiptStatus = (typeof RECEIPT_STATUSES)[number];

export const RECEIPT_STATUS_LABELS: Record<ReceiptStatus, string> = {
  pending: 'Pending',
  cleared: 'Cleared',
  bounced: 'Bounced',
  reversed: 'Reversed',
};

/** Matches the `receipt_reversals_cause_check` constraint. */
export const RECEIPT_REVERSAL_CAUSES = ['bounce', 'correction'] as const;
export type ReceiptReversalCause = (typeof RECEIPT_REVERSAL_CAUSES)[number];

/** `POST /receipts/:id/clear`. The day the bank cleared the cheque, as on the statement. */
export const receiptClearInputSchema = z.object({ clearedOn: calendarDate }).strict();
export type ReceiptClearInput = z.infer<typeof receiptClearInputSchema>;

/** `POST /receipts/:id/bounce` and `/reverse`: why, for whoever reads the audit trail later. */
export const receiptReasonInputSchema = z.object({ reason: requiredText }).strict();
export type ReceiptReasonInput = z.infer<typeof receiptReasonInputSchema>;

export interface ReceiptView {
  id: string;
  customer: { id: string; name: string };
  channel: ReceiptChannel;
  status: ReceiptStatus;
  reference: string | null;
  amountMinor: number;
  currency: (typeof CURRENCIES)[number];
  receivedOn: string;
  payer: string;
  /** The account's name as it was when the receipt was recorded; receipts from before company accounts (#446) hold what was typed. */
  bankAccount: string;
  /** Null on a receipt from before company accounts existed. */
  companyAccount: { id: string; name: string } | null;
  recordedBy: { id: string; name: string };
  createdAt: string;
}

/**
 * One receipt with the rows that changed its status (#336): `GET /receipts/:id` and the three actions answer with it, the list with plain
 * `ReceiptView`s. Each is null until it happens; a transfer or an M-Pesa receipt never has a clearance.
 */
export interface ReceiptDetailView extends ReceiptView {
  clearance: { clearedOn: string; recordedBy: { id: string; name: string }; createdAt: string } | null;
  reversal: { cause: ReceiptReversalCause; reason: string; recordedBy: { id: string; name: string }; createdAt: string } | null;
}

/**
 * `GET /receipts` — search matches the reference, the payer or the customer's name. Sorting by amount orders raw minor units across
 * currencies, so it only means something with one currency in view; nothing here adds amounts together.
 */
export const receiptListQuerySchema = defineListQuery({
  sortFields: ['receivedOn', 'amountMinor', 'createdAt', 'status'],
  defaultSort: { sort: 'receivedOn', order: 'desc' },
  filters: {
    channel: listFilter(z.enum(RECEIPT_CHANNELS)),
    status: listFilter(z.enum(RECEIPT_STATUSES)),
    companyAccountId: listFilter(z.string().uuid()),
    customerId: listFilter(z.string().uuid()),
    receivedFrom: calendarDate.optional(),
    receivedTo: calendarDate.optional(),
  },
});
export type ReceiptListQuery = z.infer<typeof receiptListQuerySchema>;
